<template>
	<div
		id="resume-form"
		class="pb-4"
	>
		<text-input
			v-model="role"
			storage-key="resume-role"
			placeholder="e.g. “Project Manager”, “Software Engineer”, “Sales Intern”"
		>
			What was your role?
		</text-input>

		<won-textarea
			v-if="form === 'resume-rewrite-form'"
			id="resume-form-points"
			v-model="current_points"
			class="mt-5"
			placeholder="e.g. “led the development of a project to reduce manual tasks...”"
			storage-key="resume-points"
		>
			Your work experience in one sentence.
		</won-textarea>

		<won-textarea
			v-if="form === 'resume-form'"
			v-model="what"
			class="mt-5"
			placeholder="e.g. led the development ..."
			storage-key="resume-what"
		>
			What did you do and why?
			<template #subtitle>
				What was a major project during your time in this role? Include titles
				and keywords.
			</template>
		</won-textarea>

		<won-textarea
			v-if="form === 'resume-form'"
			v-model="how"
			class="mt-5"
			placeholder="e.g. “using Jira, Figma, Agile...”"
			storage-key="resume-how"
		>
			How did you do it?
			<template #subtitle>Include any tools/skills you used.</template>
		</won-textarea>

		<text-input
			v-if="form === 'resume-form'"
			v-model="who"
			class="mt-5"
			placeholder="e.g. “a team of 5 developers...”"
			storage-key="resume-who"
		>
			Who did you work with?
			<template #subtitle>
				Include titles and quantify size of team if relevant.
			</template>
		</text-input>

		<won-textarea
			v-if="form === 'resume-form'"
			v-model="impact"
			class="mt-5"
			placeholder="e.g. “increased task efficiency by 20%...”"
			storage-key="resume-impact"
		>
			What did you accomplish?
			<template #subtitle>
				What was the impact? How long did it take you? How did the
				company/organization benefit?
			</template>
		</won-textarea>

		<basic-transition>
			<errors
				v-if="errors.length"
				:errors="errors"
			/>
		</basic-transition>

		<button
			:disabled="isDisabled"
			:class="[
				{
					'won-green-button-color': !isDisabled,
					'won-green-button-color-disabled': isDisabled,
				},
				buttonStyle,
				'mt-10',
			]"
			@click="handleSubmit()"
		>
			<span class="flex items-center">
				<LoadingIcon
					v-if="loading"
					class="animate-spin"
				/>

				<span class="ml-2">
					{{ loading ? 'Generating...' : 'Generate' }}
				</span>
			</span>
		</button>

		<SurveyModal
			:visible="visibleSurvey"
			link="https://docs.google.com/forms/d/e/1FAIpQLSccxfSiGYWW2gBvjLfZxBzcBoEmUU6OazNd-qupNWUeUEGLPg/viewform?usp=sf_link"
			@survey-modal:close="handleCloseSurvey()"
		/>
	</div>
</template>

<script>
import { ref, toRefs, watch } from 'vue';
import { getResumeSummary } from '../api/ai';
import { setAuthState } from '../composables/auth';
import { buttonStyle } from '../utils/styles';
import { FAKE_BPS_DATA } from '../constants/resume-tool';

import { useFeatureUsageStore } from '../store/feature-usage.store';
import { useOnboardingChecklist } from '../store/onboarding-checklist.store';
import { useAuthPopupStore } from '../store/auth-popup.store';

import TextInput from './elements/text.vue';
import WonTextarea from './elements/textarea.vue';
import LoadingIcon from '../icons/loading.vue';
import Errors from '../components/errors.vue';
import BasicTransition from '../transitions/basic.vue';
import SurveyModal from '../modals/survey';

export default {
	components: {
		TextInput,
		WonTextarea,
		LoadingIcon,
		Errors,
		BasicTransition,
		SurveyModal,
	},
	props: {
		defaultValues: {
			type: Object,
			required: false,
			default: null,
		},
		form: {
			type: String,
			required: true,
		},
		is_resume_builder: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	emits: ['resume:loading', 'resume:received'],
	setup(props) {
		const onboardingChecklistStore = useOnboardingChecklist();
		const featureUsageStore = useFeatureUsageStore();

		const { defaultValues } = toRefs(props);

		const { isAuthenticated } = setAuthState();
		const authPopupStore = useAuthPopupStore();

		const role = ref(defaultValues.value?.role ?? null);
		const current_points = ref(defaultValues.value?.current_points ?? null);

		if ( defaultValues.value?.role || defaultValues.value?.current_points ) {
			watch(() => defaultValues.value?.role, (val) => {
				role.value = val;
			});

			watch(() => defaultValues.value?.current_points, (val) => {
				current_points.value = val;
			});
		}

		// field from another form
		const what = ref('');
		const how = ref('');
		const who = ref('');
		const impact = ref('');

		const loading = ref(false);

		const errors = ref([]);

		const surveyStorageKey = 'WONSULTING-resume-survey';

		const visibleFromStorage = localStorage.getItem(surveyStorageKey) === 'true';

		const visibleSurvey = ref(visibleFromStorage || false);

		return {
			// define fields
			role,
			current_points,
			what,
			how,
			who,
			impact,

			// logic fields
			loading,
			errors,

			visibleSurvey,
			surveyStorageKey,
			featureUsageStore,
			onboardingChecklistStore,

			updateFeatureUsage: featureUsageStore.refresh,
			isAuthenticated,
			authPopupStore
		};
	},

	data: () => ({
		buttonStyle,
	}),

	computed: {
		isDisabled() {
			const isResumeFormValid =
				!this.role?.length ||
				!this.what?.length ||
				!this.how?.length ||
				!this.who?.length ||
				!this.impact?.length;

			const isResumeRewriteFormValid =
				!this.role?.length || !this.current_points?.length;

			return this.form === 'resume-form'
				? isResumeFormValid
				: isResumeRewriteFormValid;
		},
	},

	watch: {
		loading() {
			this.$emit('resume:loading', this.loading);
		},
	},

	mounted: function () {
		if (!this.isAuthenticated()) {
			return;
		}

		const savedBpData = localStorage.getItem('bpGenerationBody');

		if (savedBpData != null) {
			const jsonData = JSON.parse(savedBpData);
			localStorage.removeItem('bpGenerationBody');

			this.handleSubmit({
				role: jsonData.role,
				current_points: jsonData.current_points,
				is_resume_builder: false,
			});
		}
	},

	methods: {
		handleSubmit(customData = null) {
			if (this.loading) {
				return;
			}

			if (!this.isAuthenticated()) {
				this.loading = true;

				localStorage.setItem('bpGenerationBody', JSON.stringify({
					role: this.role,
					current_points: this.current_points
				}));

				setTimeout(() => {
					this.loading = false;
					this.authPopupStore.setShowAuthPopup();
					this.$emit('resume:received', FAKE_BPS_DATA);
				}, 500);

				return;
			}

			let data;

			if (customData != null) {
				data = customData;
			} else {
				data = this.form === 'resume-form'
					? {
						role: this.role,
						what: this.what,
						how: this.how,
						who: this.who,
						impact: this.impact,
						is_resume_builder: this.is_resume_builder,
					}
					: {
						role: this.role,
						current_points: this.current_points,
						is_resume_builder: this.is_resume_builder,
					};
			}

			const callableUrl =
				this.form === 'resume-form'
					? '/api/resume/tool'
					: '/api/resume/rewrite-tool';

			this.loading = true;
			this.errors = [];

			getResumeSummary(data, callableUrl)
				.then(({ data: { data: result } }) => {
					this.$emit('resume:received', result);
					if (callableUrl === '/api/resume/rewrite-tool') { // generate bullet points
						this.onboardingChecklistStore.markItemAsCompleted(1, 'createFirstBulletPoint');
					}
					this.updateFeatureUsage();
				})
				.catch((err) => {
					const responseData = err?.response?.data || 'No response data';
					const status = err?.response?.status || 'No status code';

					if (status === 429) {
						// here
						this.featureUsageStore.setShowLimitExceed('bullet_generation_tool');
						return;
					}

					if (status === 426) {
						this.handleShowSurvey();
						return;
					}

					if (responseData?.errors && typeof responseData.errors === 'object') {
						this.errors = Object.keys(responseData.errors).map(
							(k) => responseData.errors[k][0]
						);
					} else {
						this.errors = [responseData?.message || 'An error occurred, please try again!'];
					}
				})
				.finally(() => (this.loading = false));
		},

		handleShowSurvey() {
			this.visibleSurvey = true;
			localStorage.setItem(this.surveyStorageKey, true);
		},

		handleCloseSurvey() {
			this.visibleSurvey = false;
			localStorage.removeItem(this.surveyStorageKey);
		},
	},
};
</script>


